import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { baseApiURL } from '../../baseUrl';
import toast from 'react-hot-toast';
import { useLocation, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { FiLogOut, FiUsers, FiCalendar, FiCheckCircle, FiXCircle, FiFileText, FiBell, FiActivity } from 'react-icons/fi';

const PrincipalHome = () => {
  const router = useLocation();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('dashboard');
  const [pendingRequests, setPendingRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [rejectionReason, setRejectionReason] = useState('');
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [selectedLeave, setSelectedLeave] = useState(null);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);

  useEffect(() => {
    const activeToken = localStorage.getItem("token");
    const activeLoginId = router.state?.loginid || localStorage.getItem("loginid");
    if (!activeToken && !activeLoginId && !router.state) {
      navigate('/');
    } else {
      fetchPendingRequests();
    }
  }, [router.state, navigate]);

  const fetchPendingRequests = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${baseApiURL()}/faculty/leave/principal/pending`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.success) {
        setPendingRequests(response.data.leaves);
      }
    } catch (error) {
      toast.error("Failed to fetch pending requests");
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (leaveId) => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.put(`${baseApiURL()}/faculty/leave/principal/approve/${leaveId}`, {
        approvedBy: router.state.loginid
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.success) {
        toast.success("Leave fully approved and attendance updated");
        fetchPendingRequests();
      }
    } catch (error) {
      toast.error("Approval failed");
    }
  };

  const handleReject = async () => {
    if (!rejectionReason) {
      toast.error("Please provide a reason");
      return;
    }
    try {
      const token = localStorage.getItem("token");
      const response = await axios.put(`${baseApiURL()}/faculty/leave/principal/reject/${selectedLeave._id}`, {
        rejectionReason,
        rejectedBy: router.state.loginid
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.success) {
        toast.success("Leave rejected by Principal");
        setShowRejectModal(false);
        setRejectionReason('');
        fetchPendingRequests();
      }
    } catch (error) {
      toast.error("Rejection failed");
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950 text-slate-900 dark:text-slate-100 flex transition-colors duration-250">
      {/* Mobile Sidebar Overlay */}
      {isSidebarOpen && (
        <div 
          className="fixed inset-0 bg-slate-950/60 z-40 lg:hidden backdrop-blur-xs"
          onClick={() => setIsSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <div className={`fixed inset-y-0 left-0 w-64 bg-white dark:bg-slate-900 border-r border-slate-200 dark:border-slate-800 text-slate-900 dark:text-white flex flex-col shadow-xl lg:shadow-none transition-transform duration-300 z-50 lg:relative lg:translate-x-0 ${isSidebarOpen ? 'translate-x-0' : '-translate-x-full'}`}>
        <div className="p-5 text-center border-b border-slate-200 dark:border-slate-800">
          <h1 className="text-xl font-black tracking-tight text-slate-900 dark:text-white">Principal Panel</h1>
          <p className="text-slate-500 dark:text-slate-400 text-xs font-semibold mt-0.5">Sphoorthy Engineering College</p>
        </div>
        <nav className="flex-1 p-4 space-y-1.5">
          <button 
            onClick={() => { setActiveTab('dashboard'); setIsSidebarOpen(false); }}
            className={`w-full flex items-center space-x-3 px-4 py-2.5 rounded-xl font-bold text-xs transition cursor-pointer ${activeTab === 'dashboard' ? 'bg-indigo-600 text-white shadow-xs' : 'text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white hover:bg-slate-100 dark:hover:bg-slate-800'}`}
          >
            <FiCalendar className="text-base" /> <span>Leave Approvals</span>
            {pendingRequests.length > 0 && (
              <span className="ml-auto bg-rose-500 text-white text-[10px] px-2 py-0.5 rounded-full font-bold">{pendingRequests.length}</span>
            )}
          </button>
          <button 
            onClick={() => { setActiveTab('branches'); setIsSidebarOpen(false); }}
            className={`w-full flex items-center space-x-3 px-4 py-2.5 rounded-xl font-bold text-xs transition cursor-pointer ${activeTab === 'branches' ? 'bg-indigo-600 text-white shadow-xs' : 'text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white hover:bg-slate-100 dark:hover:bg-slate-800'}`}
          >
            <FiActivity className="text-base" /> <span>Branch Overview</span>
          </button>
          <button 
            onClick={() => { setActiveTab('reports'); setIsSidebarOpen(false); }}
            className={`w-full flex items-center space-x-3 px-4 py-2.5 rounded-xl font-bold text-xs transition cursor-pointer ${activeTab === 'reports' ? 'bg-indigo-600 text-white shadow-xs' : 'text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white hover:bg-slate-100 dark:hover:bg-slate-800'}`}
          >
            <FiFileText className="text-base" /> <span>College Reports</span>
          </button>
        </nav>
        <div className="p-4 border-t border-slate-200 dark:border-slate-800">
          <button onClick={() => navigate('/')} className="w-full flex items-center space-x-3 px-4 py-2.5 text-xs font-bold text-rose-600 dark:text-rose-400 hover:bg-rose-50 dark:hover:bg-rose-950/40 rounded-xl transition cursor-pointer">
            <FiLogOut className="text-base" /> <span>Logout</span>
          </button>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 overflow-y-auto">
        <header className="bg-white/90 dark:bg-slate-950/85 backdrop-blur-xl border-b border-slate-200 dark:border-slate-800 shadow-xs px-4 md:px-8 py-4 flex justify-between items-center sticky top-0 z-10">
          <div className="flex items-center space-x-3">
            <button 
              onClick={() => setIsSidebarOpen(true)}
              className="p-2 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-xl lg:hidden text-slate-700 dark:text-slate-300 cursor-pointer"
            >
              <FiUsers className="text-xl" />
            </button>
            <h2 className="text-base md:text-lg font-bold text-slate-900 dark:text-white tracking-tight truncate max-w-[200px] md:max-w-none">
              {activeTab === 'dashboard' ? 'Final Leave Approvals' : activeTab === 'branches' ? 'Branch Management' : 'College Analytics'}
            </h2>
          </div>
          <div className="flex items-center space-x-2 md:space-x-3">
             <button onClick={fetchPendingRequests} className="p-2 text-slate-500 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white hover:bg-slate-100 dark:hover:bg-slate-800 rounded-xl transition cursor-pointer" title="Refresh Requests">
               <FiCalendar className={`text-base md:text-lg ${loading ? 'animate-spin' : ''}`} />
             </button>
             <FiBell className="text-slate-500 dark:text-slate-400 text-base md:text-lg cursor-pointer hidden md:block" />
             <div className="w-8 h-8 md:w-9 md:h-9 bg-indigo-50 dark:bg-indigo-950/60 rounded-full flex items-center justify-center text-indigo-600 dark:text-indigo-400 font-bold border border-indigo-100 dark:border-indigo-800 text-xs">
               {router.state?.loginid?.substring(0, 2).toUpperCase() || "PR"}
             </div>
          </div>
        </header>

        <main className="p-4 md:p-6 lg:p-8">
          {activeTab === 'dashboard' && (
            <div className="space-y-6">
              {loading ? (
                <div className="flex justify-center py-20"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div></div>
              ) : pendingRequests.length === 0 ? (
                <div className="bg-white dark:bg-slate-900 rounded-2xl p-12 text-center shadow-xs border border-slate-200 dark:border-slate-800">
                  <FiCheckCircle className="mx-auto text-emerald-500 text-4xl mb-3" />
                  <h3 className="text-base font-bold text-slate-800 dark:text-slate-200">No pending approvals!</h3>
                  <p className="text-xs text-slate-400 dark:text-slate-500 mt-1">All HOD-approved leaves have been reviewed.</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 gap-4">
                  {pendingRequests.map((leave) => (
                    <motion.div 
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      key={leave._id} 
                      className="bg-white dark:bg-slate-900 rounded-2xl shadow-xs border border-slate-200 dark:border-slate-800 p-5 flex flex-col md:flex-row justify-between items-start md:items-center hover:shadow-md transition"
                    >
                      <div className="flex-1">
                        <div className="flex items-center space-x-2.5 mb-2">
                          <span className="text-base font-bold text-slate-900 dark:text-white">{leave.facultyName}</span>
                          <span className="bg-indigo-50 dark:bg-indigo-950/60 text-indigo-700 dark:text-indigo-300 text-[10px] font-bold px-2 py-0.5 rounded-lg border border-indigo-100 dark:border-indigo-800">{leave.leaveType}</span>
                          <span className="bg-amber-50 dark:bg-amber-950/60 text-amber-700 dark:text-amber-300 text-[10px] font-bold px-2 py-0.5 rounded-lg border border-amber-100 dark:border-amber-800">{leave.branch}</span>
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-y-1.5 text-xs text-slate-600 dark:text-slate-400">
                          <p><span className="font-semibold text-slate-800 dark:text-slate-200">Duration:</span> {leave.dates.length} days ({leave.startDate} to {leave.endDate})</p>
                          <p><span className="font-semibold text-slate-800 dark:text-slate-200">ID:</span> {leave.facultyId}</p>
                          <p className="col-span-2"><span className="font-semibold text-slate-800 dark:text-slate-200">Reason:</span> {leave.reason}</p>
                          <p className="col-span-2 text-[11px] text-indigo-600 dark:text-indigo-400 font-medium">Approved by HOD: {leave.hodApprovedBy} on {new Date(leave.hodApprovedAt).toLocaleDateString()}</p>
                        </div>
                      </div>
                      <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-2.5 mt-4 sm:mt-0 sm:ml-6">
                        <button 
                          onClick={() => handleApprove(leave._id)}
                          className="flex items-center justify-center space-x-1.5 bg-emerald-600 hover:bg-emerald-500 text-white px-4 py-2 rounded-xl transition shadow-xs text-xs font-bold cursor-pointer"
                        >
                          <FiCheckCircle /> <span>Final Approve</span>
                        </button>
                        <button 
                          onClick={() => { setSelectedLeave(leave); setShowRejectModal(true); }}
                          className="flex items-center justify-center space-x-1.5 bg-rose-600 hover:bg-rose-500 text-white px-4 py-2 rounded-xl transition shadow-xs text-xs font-bold cursor-pointer"
                        >
                          <FiXCircle /> <span>Reject</span>
                        </button>
                      </div>
                    </motion.div>
                  ))}
                </div>
              )}
            </div>
          )}

          {activeTab === 'branches' && (
             <div className="bg-white dark:bg-slate-900 p-8 rounded-2xl shadow-xs border border-slate-200 dark:border-slate-800 text-center">
                <FiActivity className="mx-auto text-indigo-600 dark:text-indigo-400 text-4xl mb-3" />
                <h3 className="text-base font-bold text-slate-900 dark:text-white">Branch Overview</h3>
                <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">Monitor all college departments and their current status.</p>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-6">
                    {['CSE', 'ECE', 'EEE', 'MECH'].map(branch => (
                        <div key={branch} className="p-4 bg-slate-50 dark:bg-slate-950 rounded-xl border border-slate-200 dark:border-slate-800">
                            <span className="block font-bold text-base text-slate-900 dark:text-white">{branch}</span>
                            <span className="text-xs text-emerald-600 dark:text-emerald-400 font-bold mt-1 block">95% Attendance</span>
                        </div>
                    ))}
                </div>
             </div>
          )}
          
          {activeTab === 'reports' && (
             <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="bg-white dark:bg-slate-900 p-6 rounded-2xl shadow-xs border border-slate-200 dark:border-slate-800">
                   <h3 className="font-bold text-xs uppercase tracking-wider text-slate-900 dark:text-white mb-4">Overall Faculty Attendance</h3>
                   <div className="h-44 bg-slate-50 dark:bg-slate-950 rounded-xl border border-slate-200 dark:border-slate-800 flex items-end justify-around p-4">
                      {[75, 85, 70, 95, 80, 90].map((h, i) => (
                        <div key={i} className="w-8 bg-indigo-600 dark:bg-indigo-500 rounded-t-lg" style={{ height: `${h}%` }}></div>
                      ))}
                   </div>
                </div>
                <div className="bg-white dark:bg-slate-900 p-6 rounded-2xl shadow-xs border border-slate-200 dark:border-slate-800">
                   <h3 className="font-bold text-xs uppercase tracking-wider text-slate-900 dark:text-white mb-4">Leave Trends by Branch</h3>
                   <div className="space-y-3">
                        {['CSE', 'ECE', 'EEE'].map(b => (
                            <div key={b} className="flex items-center text-xs font-semibold">
                                <span className="w-12 text-slate-700 dark:text-slate-300 font-bold">{b}</span>
                                <div className="flex-1 h-2.5 bg-slate-100 dark:bg-slate-800 rounded-full mx-3 overflow-hidden">
                                    <div className="h-full bg-indigo-600 dark:bg-indigo-500" style={{ width: b === 'CSE' ? '70%' : b === 'ECE' ? '45%' : '30%' }}></div>
                                </div>
                                <span className="text-[11px] text-slate-400 dark:text-slate-500">View</span>
                            </div>
                        ))}
                   </div>
                </div>
             </div>
          )}
        </main>
      </div>

      {/* Rejection Modal */}
      <AnimatePresence>
        {showRejectModal && (
          <div className="fixed inset-0 bg-slate-950/60 flex items-center justify-center z-50 p-4 backdrop-blur-sm">
            <motion.div 
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              className="bg-white dark:bg-slate-900 rounded-2xl p-6 w-full max-w-md shadow-2xl border border-slate-200 dark:border-slate-800"
            >
              <h3 className="text-lg font-bold text-slate-900 dark:text-white mb-1">Principal Rejection</h3>
              <p className="text-slate-500 dark:text-slate-400 mb-4 text-xs">Reason for rejecting {selectedLeave?.facultyName}'s leave from {selectedLeave?.branch} department.</p>
              <textarea 
                value={rejectionReason}
                onChange={(e) => setRejectionReason(e.target.value)}
                className="w-full p-3.5 bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-xl focus:ring-2 focus:ring-rose-500 outline-none text-xs text-slate-900 dark:text-white mb-4 h-28 resize-none"
                placeholder="Enter rejection reason..."
              />
              <div className="flex space-x-2.5">
                <button onClick={() => setShowRejectModal(false)} className="flex-1 py-2.5 border border-slate-200 dark:border-slate-800 rounded-xl text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 text-xs font-bold transition cursor-pointer">Cancel</button>
                <button onClick={handleReject} className="flex-1 py-2.5 bg-rose-600 text-white rounded-xl hover:bg-rose-500 text-xs font-bold transition cursor-pointer shadow-xs">Confirm Rejection</button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default PrincipalHome;
